<template>
  <div class="page-content">
    <!-- Header -->
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Utilisateurs</h1>
      </div>
      <v-btn v-if="!isSupervisor" color="primary" depressed @click="dialog = true">
        <v-icon left small>mdi-plus</v-icon>
        Nouvel utilisateur
      </v-btn>
    </div>

    <!-- Loading -->
    <PreloaderComponent :enable="enable" />

    <!-- Search -->
    <div v-if="enable" class="d-flex justify-end mb-3">
      <input
        v-model="search"
        type="text"
        placeholder="Rechercher..."
        class="search-input"
      />
    </div>

    <!-- Data table -->
    <v-card flat v-if="enable" class="table-card">
      <v-data-table
        :headers="headers"
        :items="rows"
        :items-per-page="10"
        :search="search"
        :loading="tableLoading"
        loading-text="Chargement..."
        no-data-text="Aucun utilisateur trouvé"
        :footer-props="{
          'items-per-page-options': [5, 10, 15, 20, -1],
          'items-per-page-text': 'Par page',
        }"
      >
        <template v-slot:item.name="{ item }">
          <div>
            <div class="font-weight-medium">{{ item.full_name }}</div>
            <div class="text-caption text--secondary">{{ item.username }}</div>
          </div>
        </template>

        <template v-slot:item.contact="{ item }">
          <div>
            <div class="text-body-2">{{ item.email || '--' }}</div>
            <div class="text-caption text--secondary">{{ item.phone || '--' }}</div>
          </div>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip :color="item.status ? 'success' : 'error'" small dark class="status-chip">
            {{ item.status ? 'Actif' : 'Inactif' }}
          </v-chip>
        </template>

        <template v-slot:item.rate_id="{ item }">
          {{ getRateLabel(item.rate_id) }}
        </template>

        <template v-slot:item.role.name="{ item }">
          <v-chip color="primary" small dark outlined class="status-chip">
            {{ getRoleLabel(item) }}
          </v-chip>
        </template>

        <template v-slot:item.discount_limit="{ item }">
          {{ item.discount_limit }}%
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn v-if="canEditUser(item)" icon small @click="edit(item)">
            <v-icon small color="primary">mdi-pencil</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create/Edit dialog -->
    <v-dialog v-model="dialog" max-width="640" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">
          {{ form_title }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-form ref="form" v-model="isFormValid">
            <v-row>
              <v-col cols="12">
                <v-text-field v-model="user.full_name" label="Nom complet *" :rules="nameRules" outlined dense hide-details="auto"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="user.email" label="E-mail *" :rules="emailRules" outlined dense hide-details="auto"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="user.phone" label="Téléphone" outlined dense hide-details="auto"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="user.username" label="Nom d'utilisateur *" :rules="usernameRules" outlined dense hide-details="auto"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="user.password" :label="isEditMode ? 'Pin (laisser vide pour garder)' : 'Pin *'" type="password" :rules="isEditMode ? passwordEditRules : passwordRules" outlined dense hide-details="auto"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select v-model="user.role_id" :items="roles" item-text="name" item-value="id" label="Rôle *" :rules="[v => !!v || 'Requis']" :disabled="isSupervisor" outlined dense hide-details="auto"></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select v-model="user.status" :items="status" item-text="name" item-value="id" label="Statut *" :rules="[v => v !== '' || 'Requis']" outlined dense hide-details="auto"></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model.number="user.commission" label="Commission *" type="number" :rules="commissionRules" outlined dense hide-details="auto"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select v-model="user.rate_id" :items="rateinfo" item-text="name" item-value="id" label="Devise de vente *" :rules="[v => v !== '' || 'Requis']" outlined dense hide-details="auto"></v-select>
              </v-col>
              <v-col cols="12">
                <v-select v-model="user.discount_limit" :items="discountsItems" label="Limite de rabais *" :rules="[v => v !== '' || 'Requis']" outlined dense hide-details="auto"></v-select>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="px-6 py-3">
          <v-spacer></v-spacer>
          <v-btn text @click="close">Fermer</v-btn>
          <v-btn color="primary" depressed :loading="loading" :disabled="loading || !isFormValid" @click="save">
            <v-icon left small>mdi-content-save</v-icon>
            Valider
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <FlashMessage :position="'right bottom'"></FlashMessage>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import PreloaderComponent from '../components/PreloaderComponent.vue'
import { isCurrentUserSupervisor, getAuthenticatedUser, isCashierRole } from '../utils/accessControl'

export default {
  name: 'UsersView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data() {
    return {
      search: '',
      isFormValid: false,
      tableLoading: false,
      status: [{ name: "Actif", id: true }, { name: 'Inactif', id: false }],
      rateinfo: [{ name: "HTG / USD", id: 0 }, { name: 'HTG', id: 1 }, { name: 'USD', id: 2 }],
      index: -1,
      loading: false,
      form_title: "Nouvel Utilisateur",
      items: [{ text: 'Utilisateurs', disabled: false, href: '/users' }],
      enable: false,
      headers: [
        { text: 'NOM', align: 'start', sortable: true, value: 'name', width: '25%' },
        { text: 'CONTACT', value: 'contact' },
        { text: 'ROLE', value: 'role.name' },
        { text: 'STATUT', value: 'status' },
        { text: 'DEVISE', value: 'rate_id' },
        { text: 'RABAIS', value: 'discount_limit' },
        { text: '', value: 'actions', sortable: false, width: '60px' },
      ],
      rows: [],
      nameRules: [v => !!v || 'Le nom ne peut pas être vide'],
      emailRules: [v => !!v || "L'email est requis", v => /.+@.+\..+/.test(v) || "L'email doit être valide"],
      usernameRules: [v => !!v || "Le nom d'utilisateur est requis", v => /^[a-zA-Z][a-zA-Z0-9._]{2,}$/.test(v) || 'Min 3 caractères, commence par une lettre'],
      passwordRules: [v => !!v || 'Le mot de passe est requis', v => v.length >= 6 || '6 caractères minimum', v => !isNaN(parseInt(v)) || 'Chiffres uniquement'],
      passwordEditRules: [v => !v || v.length >= 6 || '6 caractères minimum', v => !v || !isNaN(parseInt(v)) || 'Chiffres uniquement'],
      commissionRules: [v => v !== '' || 'La commission est requise'],
      roles: [],
      dialog: false,
      user: { username: '', password: '', full_name: '', commission: 0, rate_id: 1, phone: '', email: '', business_id: 0, role_id: '', discount_limit: 0, status: '' },
      discounts: [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100]
    }
  },
  computed: {
    discountsItems() { return [0, ...this.discounts].map(d => ({ text: `${d} %`, value: d })) },
    isEditMode() { return this.index > -1 },
    isSupervisor() { return isCurrentUserSupervisor() }
  },
  methods: {
    load() {
      this.tableLoading = true
      this.$axios.get('/users?include=role&orderasc=full_name')
        .then(response => { this.rows = response.data; this.enable = true; this.tableLoading = false })
        .catch(error => { console.error('Erreur:', error); this.tableLoading = false })
    },
    getRateLabel(rate) { return { 0: 'HTG / USD', 1: 'HTG', 2: 'USD' }[rate] || '' },
    getRoleLabel(item) { const role = this.roles.find(r => r.id === item.role_id); return role ? role.name : 'ADMIN' },
    canEditUser(item) {
      if (!this.isSupervisor) return true
      const currentUser = getAuthenticatedUser()
      if (currentUser && item.id === currentUser.id) return true
      return isCashierRole(this.getRoleLabel(item))
    },
    save() {
      this.loading = true
      if (this.index > -1) {
        const original = this.rows[this.index]
        if (this.isSupervisor && !this.canEditUser(original)) {
          this.showError('Les superviseurs ne peuvent modifier que leur compte et les comptes caissiers.')
          this.loading = false
          return
        }
        const userToSend = { ...this.user }
        if (this.isSupervisor) userToSend.role_id = original.role_id
        if (!userToSend.password || userToSend.password.trim() === '') delete userToSend.password
        const payload = { data: { ...userToSend } }
        delete payload.data.role
        this.$axios({ method: 'put', url: `/users/${this.user.id}?include=role`, data: payload })
          .then(response => {
            if (response.status >= 200 && response.status < 300) {
              Object.assign(this.rows[this.index], response.data)
              this.dialog = false
              this.flashMessage.success({ title: 'Succès', message: 'Enregistré avec succès' })
              this.reset()
            } else { this.showError("Erreur. Veuillez réessayer.") }
          })
          .catch(() => { this.showError("Erreur. Veuillez réessayer.") })
          .finally(() => { this.loading = false })
      } else {
        if (this.isSupervisor) {
          this.showError("Les superviseurs ne peuvent pas ajouter d'utilisateur.")
          this.loading = false
          return
        }
        this.$axios.post('/users', this.user)
          .then(response => {
            if (response.status >= 200 && response.status < 300) {
              this.rows.push(response.data)
              this.dialog = false
              this.flashMessage.success({ title: 'Succès', message: 'Enregistré avec succès' })
              this.reset()
            } else { this.showError("Erreur. Veuillez réessayer.") }
          })
          .catch(() => { this.showError("Erreur. Veuillez réessayer.") })
          .finally(() => { this.loading = false })
      }
    },
    edit(item) {
      if (!this.canEditUser(item)) {
        this.showError('Les superviseurs ne peuvent modifier que leur compte et les comptes caissiers.')
        return
      }
      this.index = this.rows.indexOf(item)
      this.user = Object.assign({}, item)
      this.user.password = ''
      this.form_title = `Modifier : ${this.user.full_name}`
      this.dialog = true
    },
    getRoles() {
      this.$axios.get('/roles').then(response => { this.roles = response.data }).catch(error => { console.error('Erreur rôles:', error) })
    },
    close() { this.dialog = false; this.reset() },
    reset() {
      this.$refs.form && this.$refs.form.reset()
      this.user = { username: '', password: '', full_name: '', commission: 0, rate_id: 1, phone: '', email: '', business_id: 0, role_id: '', discount_limit: 0, status: '' }
      this.index = -1
      this.form_title = 'Nouvel Utilisateur'
      this.loading = false
    },
    showError(message) { alert(message) }
  },
  mounted() { this.load(); this.getRoles() }
}
</script>

<style scoped>
.search-input {
  width: 260px;
  padding: 8px 12px;
  font-size: 0.8125rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  outline: none;
  color: #1a2332;
  background: #fff;
}

.search-input:focus {
  border-color: #194173;
  box-shadow: 0 0 0 2px rgba(25, 65, 115, 0.1);
}

.search-input::placeholder {
  color: #94a3b8;
}

.table-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important;
}

</style>
